// Format checks for portal forms. They mirror the server's rules
// (packages/core/src/normalise.js, constants.js) so people hear about a typo before
// they press Save; the server still checks everything.
//
// Each check returns '' when the value is fine, or a short message saying what to do.
// `required: false` lets an empty value through (optional fields).

import { FREE_MAIL_DOMAINS } from '@summitshield/core/constants';

const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[a-z]{2,}$/i;
const HOST_RE = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;

const empty = (v) => !String(v ?? '').trim();

/** "https://www.TechSummit.events/tickets?x" → "techsummit.events" */
export function cleanDomain(v) {
  return String(v ?? '').trim().toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/^www\d*\./, '')
    .replace(/[/?#:].*$/, '')
    .replace(/^@/, '')
    .replace(/\.$/, '');
}

export function checkEmail(v, { required = true, label = 'email address' } = {}) {
  if (empty(v)) return required ? `Enter the ${label}.` : '';
  return EMAIL_RE.test(String(v).trim()) ? '' : 'That doesn’t look like an email address — it should look like name@company.com.';
}

export function checkDomain(v, { required = true } = {}) {
  if (empty(v)) return required ? 'Enter the domain, e.g. techsummit.events.' : '';
  const d = cleanDomain(v);
  if (d.includes('@')) return 'Enter just the domain — the part after the @, e.g. techsummit.events.';
  return HOST_RE.test(d) ? '' : 'That doesn’t look like a domain — it should look like techsummit.events.';
}

/** A domain that must be the company's own: never a free email service. */
export function checkOwnDomain(v, opts = {}) {
  const problem = checkDomain(v, opts);
  if (problem) return problem;
  const d = cleanDomain(v);
  if (d && FREE_MAIL_DOMAINS.has(d)) return `${d} is a free email service, so it can’t be your own domain. Add the address itself as an email address instead.`;
  return '';
}

/** What the person will see saved, when cleaning changed what they typed. */
export function cleanedHint(v) {
  const d = cleanDomain(v);
  return d && d !== String(v ?? '').trim().toLowerCase() ? `We’ll save this as ${d}.` : '';
}

export function checkUrl(v, { required = true } = {}) {
  if (empty(v)) return required ? 'Enter the web address.' : '';
  const s = String(v).trim();
  let host;
  try { host = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(s) ? s : `https://${s}`).hostname; } catch { host = ''; }
  return HOST_RE.test(host.replace(/^www\d*\./, '')) || HOST_RE.test(host) ? '' : 'That doesn’t look like a web address — e.g. techsummit.events/hotels.';
}

/** Loose check: the server parses the number properly (UK by default). */
export function checkPhone(v, { required = true } = {}) {
  if (empty(v)) return required ? 'Enter the phone number.' : '';
  const s = String(v).trim();
  if (/[^\d\s()+.-]/.test(s)) return 'Use only numbers, spaces and + ( ) - in a phone number.';
  const digits = s.replace(/\D/g, '');
  if (digits.length < 9 || digits.length > 15) return 'That doesn’t look like a full phone number. Include the area code, e.g. 020 7946 0018 or +44 20 7946 0018.';
  return '';
}

function ibanOk(iban) {
  const moved = iban.slice(4) + iban.slice(0, 4);
  const numeric = moved.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  let rem = 0;
  for (const ch of numeric) rem = (rem * 10 + Number(ch)) % 97;
  return rem === 1;
}

/** An IBAN, or a UK sort code + 8-digit account number. */
export function checkBank(v, { required = true } = {}) {
  if (empty(v)) return required ? 'Enter the bank details.' : '';
  const compact = String(v).replace(/\s+/g, '').toUpperCase();
  if (/^[A-Z]{2}\d{2}/.test(compact)) {
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(compact)) return 'That IBAN is the wrong length — check it against your bank statement.';
    return ibanOk(compact) ? '' : 'That IBAN doesn’t add up — check for a mistyped character.';
  }
  return /^(\d{2})[-\s]?(\d{2})[-\s]?(\d{2})[\s/-]*(\d{8})$/.test(String(v).trim())
    ? '' : 'Enter an IBAN (GB82 WEST 1234 5698 7654 32) or a UK sort code and 8-digit account number (12-34-56 12345678).';
}

export function checkSlug(v) {
  if (empty(v)) return 'Choose the web address.';
  return SLUG_RE.test(v) ? '' : 'Use 3–50 lower-case letters, numbers and hyphens, starting and ending with a letter or number.';
}

export function checkRequired(v, label, { max } = {}) {
  if (empty(v)) return `Enter ${label}.`;
  if (max && String(v).trim().length > max) return `Keep it to ${max} characters or fewer.`;
  return '';
}

/** End date on or after the start date (both "YYYY-MM-DD", either may be blank). */
export function checkDateOrder(start, end) {
  if (start && end && end < start) return 'The end date can’t be before the start date.';
  return '';
}

/** Run checks: { field: message } for the ones that failed (empty object = all fine). */
export function collectErrors(checks) {
  return Object.fromEntries(Object.entries(checks).filter(([, msg]) => msg));
}
